import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Search, Download } from "lucide-react";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { ToastContext } from "../../Context/ToastContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const safeJSON = (v, fb = null) => {
  try {
    return typeof v === "string" ? JSON.parse(v) : v ?? fb;
  } catch {
    return fb;
  }
};

const formatHrs = (duration) => {
  if (!duration || typeof duration !== "string") return "0h 0m";
  const m = duration.match(/(\d+)h\s*(\d+)m/);
  if (m) return `${parseInt(m[1], 10)}h ${parseInt(m[2], 10)}m`;
  const num = duration.match(/^\d+(\.\d+)?$/);
  if (num) {
    const hours = parseFloat(num[0]);
    return `${Math.floor(hours)}h ${Math.round((hours % 1) * 60)}m`;
  }
  return duration;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    // Handle ISO format with time (e.g., "2025-10-24T17:12:19.299Z")
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    // Format to dd-mm-yyyy
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch {
    return "Invalid Date";
  }
};

const daysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const s = new Date(startDate),
    e = new Date(endDate);
  if (isNaN(s) || isNaN(e)) return 0;
  const diff = e.getTime() - s.getTime();
  const days = Math.ceil(diff / (1000 * 3600 * 24)) + 1;
  return Math.max(days, 0);
};

const salaryMonth = (startDate, endDate) => {
  if (!startDate || !endDate) return "N/A";
  const s = new Date(startDate),
    e = new Date(endDate);
  if (isNaN(s) || isNaN(e)) return "Invalid Date";
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return s.toLocaleString("default", { month: "long" });
  }
  const startMonthDays =
    new Date(s.getFullYear(), s.getMonth() + 1, 0).getDate() - s.getDate() + 1;
  const endMonthDays = e.getDate();
  return startMonthDays >= endMonthDays
    ? s.toLocaleString("default", { month: "long" })
    : e.toLocaleString("default", { month: "long" });
};

const buildEmployeeMaps = (salaries, employees) => {
  const idxByMobile = new Map();
  (employees || []).forEach((emp) => {
    const mobile = (emp?.mobile_number || emp?.mobileNumber || "")
      .toString()
      .trim();
    if (mobile) idxByMobile.set(mobile, emp);
  });

  const employeeDetailsMap = {};
  const missingDetailsMap = {};

  (salaries || []).forEach((salary) => {
    const mobile = (salary?.mobileNumber || salary?.userMobile || "")
      .toString()
      .trim();
    const emp = mobile ? idxByMobile.get(mobile) : null;

    if (!emp) {
      employeeDetailsMap[salary.id] = {
        locationName: "N/A",
        address: "N/A",
        shiftName: "N/A",
        mobileNumber: mobile || "N/A",
      };
      missingDetailsMap[salary.id] = { error: "No employee data found" };
      return;
    }

    let shiftName = "N/A";
    const parsedShift = safeJSON(emp.shift, null);
    if (Array.isArray(parsedShift) && parsedShift.length) {
      shiftName = parsedShift[0]?.shiftName || "N/A";
    }

    employeeDetailsMap[salary.id] = {
      locationName: emp.locationName || "N/A",
      address: emp.address || "N/A",
      shiftName,
      mobileNumber: emp.mobile_number || emp.mobileNumber || mobile || "N/A",
    };

    const basic = [];
    const bank = [];
    const sal = [];

    if (!emp.full_name) basic.push("Name");
    if (!emp.employee_id) basic.push("Employee ID");
    if ("jobTitle" in emp && !emp.jobTitle) basic.push("Designation");
    if ("dateOfJoining" in emp && !emp.dateOfJoining)
      basic.push("Date of Joining");

    if ("bankName" in emp || "bank_name" in emp) {
      if (!emp.bankName && !emp.bank_name) bank.push("Bank Name");
    }
    if ("accountNumber" in emp || "account_number" in emp) {
      if (!emp.accountNumber && !emp.account_number)
        bank.push("Account Number");
    }
    if ("ifscCode" in emp || "ifsc_code" in emp) {
      if (!emp.ifscCode && !emp.ifsc_code) bank.push("IFSC Code");
    }

    if (!salary.startDate || !salary.endDate) sal.push("Pay Period Dates");
    if (!salary.overallcalculatedSalary) sal.push("Salary Amount");

    missingDetailsMap[salary.id] = {
      basic: basic.length ? basic : null,
      bank: bank.length ? bank : null,
      salary: sal.length ? sal : null,
    };
  });

  return { employeeDetailsMap, missingDetailsMap };
};

const PayslipManagement = () => {
  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } =
    getActionPermissions(userRole);
  const [salaryData, setSalaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [shiftFilter, setShiftFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [employeeDetailsMap, setEmployeeDetailsMap] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [currentSalaryId, setCurrentSalaryId] = useState(null);
  const [currentSalaryData, setCurrentSalaryData] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState("");
  const [payslipFilter, setPayslipFilter] = useState("all");
  const [daysFilter, setDaysFilter] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [locationsData, setLocationsData] = useState([]);
  const [missingDetailsMap, setMissingDetailsMap] = useState({});

  const { addToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [salaryToDelete, setSalaryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const companyKey = sessionStorage.getItem("companyKey");

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      try {
        const userMobile =
          localStorage.getItem("userMobile") ||
          sessionStorage.getItem("userMobile");
        if (!userMobile) {
          console.warn("No user mobile found in storage");
          return;
        }

        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList"
        );
        if (!response.ok) {
          throw new Error("Failed to fetch admin data");
        }

        const data = await response.json();
        const adminList = Array.isArray(data) ? data : [data];
        const currentUser = adminList.find(
          (admin) => admin.mobileNumber === userMobile
        );
        if (currentUser) {
          setCurrentUserRole(currentUser.role);
        } else {
          console.warn("Current user not found in admin list");
        }
      } catch (error) {
        console.error("Error fetching admin role:", error);
        addToast("Error fetching admin role", "error");
      }
    };

    fetchCurrentUserRole();
  }, [addToast]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        setError("");
        const salRes = await fetch(
          "https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList"
        );
        if (!salRes.ok)
          throw new Error(`Salary server error: ${salRes.status}`);
        const salJson = await salRes.json();
        const salaries = Array.isArray(salJson)
          ? [...salJson].reverse()
          : [salJson];
        const filteredSalaries = salaries.filter(
          (sal) => sal.companyKey === companyKey
        );
        setSalaryData(filteredSalaries);

        const locRes = await fetch(
          "https://api.dremerz.net/api/DZ_8922_PayrollProduct_PayrollAddLocations_PayrollLocationDetails"
        );
        if (!locRes.ok) throw new Error(`Locations error: ${locRes.status}`);
        const locJson = await locRes.json();
        const locations = Array.isArray(locJson) ? locJson : [locJson];
        const filteredLocations = locations.filter(
          (loc) => loc.companyKey === companyKey
        );
        setLocationsData(filteredLocations);

        const empRes = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
        );
        if (!empRes.ok) throw new Error(`Employees error: ${empRes.status}`);
        const empJson = await empRes.json();
        const employees = Array.isArray(empJson) ? empJson : [empJson];
        const filteredEmployees = employees.filter(
          (emp) => emp.companyKey === companyKey
        );
        const maps = buildEmployeeMaps(filteredSalaries, filteredEmployees);
        setEmployeeDetailsMap(maps.employeeDetailsMap);
        setMissingDetailsMap(maps.missingDetailsMap);
      } catch (err) {
        console.error(err);
        const msg = err?.message || "Failed to load data";
        setError(msg);
        addToast(msg, "error");
        setSalaryData([]);
        setLocationsData([]);
        setEmployeeDetailsMap({});
        setMissingDetailsMap({});
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  useEffect(() => {
    fetchSalaryData();
    fetchLocationsData();
  }, []);

  const fetchLocationsData = async () => {
    try {
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8922_PayrollProduct_PayrollAddLocations_PayrollLocationDetails"
      );
      if (!response.ok) {
        throw new Error(`Error fetching locations: ${response.status}`);
      }
      const data = await response.json();
      const locations = Array.isArray(data) ? data : [data];
      const filteredLocations = locations.filter(
        (loc) => loc.companyKey === companyKey
      );
      setLocationsData(filteredLocations);
    } catch (error) {
      console.error("Error fetching locations:", error);
      setError(error.message || "Failed to fetch locations data");
      addToast(error.message || "Failed to fetch locations data", "error");
      setLocationsData([]);
    }
  };

  const fetchSalaryData = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList"
      );

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      const processedData = Array.isArray(data) ? [...data].reverse() : [data];
      const filteredData = processedData.filter(
        (sal) => sal.companyKey === companyKey
      );
      setSalaryData(filteredData);
    } catch (error) {
      console.error("Error fetching salary data:", error);
      setError(error.message || "Failed to fetch salary data");
      addToast(error.message || "Failed to fetch salary data", "error");
      setSalaryData([]);
    } finally {
      setLoading(false);
    }
  };

  // PDF Download Function
  const handleDownloadPdf = async (
    pdfUrl,
    salaryId,
    employeeName = "payslip"
  ) => {
    if (!pdfUrl || typeof pdfUrl !== "string") {
      addToast("No PDF available for download", "warning");
      return;
    }

    try {
      setDownloadingPdf(salaryId);

      const cleanName = (employeeName || "payslip").replace(
        /[^a-zA-Z0-9]/g,
        "_"
      );
      const timestamp = new Date().toISOString().split("T")[0];
      const fileName = `${cleanName}_payslip_${timestamp}.pdf`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(pdfUrl, {
        signal: controller.signal,
        headers: {
          Accept: "application/pdf,application/octet-stream,*/*",
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(
          `Failed to download PDF: ${response.status} ${response.statusText}`
        );
      }
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("pdf")) {
        throw new Error("Response is not a PDF file");
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Downloaded file is empty");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addToast("Payslip downloaded successfully", "success");
    } catch (error) {
      console.error("Download error:", error);
      if (error.name === "AbortError") {
        addToast("Download timed out. Please try again.", "error");
      } else {
        addToast(`Failed to download payslip: ${error.message}`, "error");
      }
    } finally {
      setDownloadingPdf(null);
    }
  };

  const isSuperAdmin = currentUserRole === "superadmin";

  const handleSearch = (e) => {
    try {
      const value = e?.target?.value || "";
      setSearchTerm(value);
      setCurrentPage(1);
    } catch (error) {
      console.error("Search error:", error);
      setSearchTerm("");
    }
  };

  const clearAllFilters = () => {
    try {
      setSearchTerm("");
      setLocationFilter("");
      setShiftFilter("");
      setMonthFilter("");
      setDaysFilter("");
      setPayslipFilter("all");
      setCurrentPage(1);
    } catch (error) {
      console.error("Error clearing filters:", error);
    }
  };

  const handleOpenPdfModal = (pdfUrl) => {
    if (!pdfUrl || typeof pdfUrl !== "string") {
      addToast("No PDF available for this payslip", "warning");
      return;
    }

    // Validate URL format
    try {
      new URL(pdfUrl);
      setCurrentPdfUrl(pdfUrl);
      setShowPdfModal(true);
    } catch (error) {
      console.error("Invalid PDF URL:", error);
      addToast("Invalid PDF URL format", "error");
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setCurrentPdfUrl("");
  };

  const handleGeneratePayslip = (salaryId) => {
    if (!salaryId) {
      addToast("Invalid salary ID", "error");
      return;
    }

    try {
      navigate(`/payslip-generate/${salaryId}`);
    } catch (error) {
      console.error("Navigation error:", error);
      addToast("Failed to navigate to payslip generation", "error");
    }
  };

  const handleUpdateStatusClick = (salaryId, salary) => {
    if (!salaryId) {
      addToast("Invalid salary ID", "error");
      return;
    }

    setCurrentSalaryId(salaryId);
    setCurrentSalaryData(salary);
    setStatusMessage("");
    setShowStatusModal(true);
  };

  const handleStatusSubmit = async () => {
    if (!currentSalaryId || !statusMessage.trim()) {
      addToast("Please enter a response message", "warning");
      return;
    }

    try {
      setIsUpdatingStatus(true);

      const response = await fetch(
        `https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList/${currentSalaryId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            payslipResponse: statusMessage.trim(),
            requestforpayslip: "responded",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update status: ${response.status}`);
      }

      doAuditLog("Update", "Payslip Generator", currentSalaryData, {
        ...currentSalaryData,
        payslipResponse: statusMessage.trim(),
        requestforpayslip: "responded",
      });
      await fetchSalaryData();
      setShowStatusModal(false);
      setStatusMessage("");
      addToast("Status updated successfully", "success");
    } catch (error) {
      console.error("Error updating status:", error);
      addToast("Failed to update status. Please try again.", "error");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDeleteClick = (salary) => {
    setSalaryToDelete(salary);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!salaryToDelete?.id) return;

    try {
      setIsDeleting(true);
      const response = await fetch(
        `https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList/${salaryToDelete.id}`,
        { method: "DELETE" }
      );

      if (!response.ok) throw new Error(`Delete failed: ${response.status}`);
      doAuditLog("Delete", "Payslip Generator", salaryToDelete, "");
      await fetchSalaryData();
      setShowDeleteModal(false);
      setSalaryToDelete(null);
      addToast("Payslip deleted successfully", "success");
    } catch (error) {
      console.error("Delete error:", error);
      addToast("Failed to delete payslip", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditPayslip = (salaryId) => {
    if (!salaryId) {
      addToast("Invalid salary ID", "error");
      return;
    }

    try {
      // Navigate to the same payslip generation page for editing
      navigate(`/payslip-generate/${salaryId}`);
    } catch (error) {
      console.error("Navigation error:", error);
      addToast("Failed to navigate to edit payslip", "error");
    }
  };

  const getSalaryMonth = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Check if dates are valid
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return "Invalid Date";
      }

      if (
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear()
      ) {
        return start.toLocaleString("default", { month: "long" });
      }

      const startMonthDays =
        new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate() -
        start.getDate() +
        1;
      const endMonthDays = end.getDate();

      return startMonthDays >= endMonthDays
        ? start.toLocaleString("default", { month: "long" })
        : end.toLocaleString("default", { month: "long" });
    } catch (error) {
      console.error("Error calculating salary month:", error);
      return "Error";
    }
  };

  const calculateDaysBetween = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Check if dates are valid
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 0;
      }

      const timeDifference = end.getTime() - start.getTime();
      const dayDifference = Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1;

      return dayDifference > 0 ? dayDifference : 0;
    } catch (error) {
      console.error("Error calculating days between dates:", error);
      return 0;
    }
  };

  const formatTimeDuration = (duration) => {
    if (!duration || typeof duration !== "string") return "0h 0m";

    try {
      // Handle different duration formats
      const match = duration.match(/(\d+)h\s*(\d+)m/);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        return `${hours}h ${minutes}m`;
      }

      // If it's just a number, assume it's hours
      const numMatch = duration.match(/^\d+(\.\d+)?$/);
      if (numMatch) {
        const hours = parseFloat(numMatch[0]);
        return `${Math.floor(hours)}h ${Math.round((hours % 1) * 60)}m`;
      }

      return duration;
    } catch (error) {
      console.error("Error formatting time duration:", error);
      return "0h 0m";
    }
  };

  const filteredSalaryData = salaryData.filter((salary) => {
    // Apply payslip filter first
    if (payslipFilter === "generated" && !salary.payslipPdf) return false;
    if (payslipFilter === "pending" && salary.payslipPdf) return false;

    if (
      !searchTerm &&
      !locationFilter &&
      !shiftFilter &&
      !monthFilter &&
      !daysFilter
    )
      return true;

    const searchLower = searchTerm.toLowerCase();
    const empDetails = employeeDetailsMap[salary.id] || {};

    // Enhanced search functionality
    const matchesSearch =
      !searchTerm ||
      // User ID search
      (salary.userId &&
        salary.userId.toString().toLowerCase().includes(searchLower)) ||
      // User name search
      (salary.userName &&
        salary.userName.toLowerCase().includes(searchLower)) ||
      // Mobile number search (check both salary.mobileNumber and salary.userMobile)
      (salary.mobileNumber &&
        salary.mobileNumber.toLowerCase().includes(searchLower)) ||
      (salary.userMobile &&
        salary.userMobile.toLowerCase().includes(searchLower)) ||
      // Employee ID search
      (salary.employee_id &&
        salary.employee_id.toString().toLowerCase().includes(searchLower)) ||
      // Pay period search (search in both start and end dates)
      (salary.startDate &&
        salary.startDate.toLowerCase().includes(searchLower)) ||
      (salary.endDate && salary.endDate.toLowerCase().includes(searchLower)) ||
      // Pay period range search (e.g., "2024-01" will match dates in January 2024)
      (salary.startDate &&
        salary.endDate &&
        `${salary.startDate} to ${salary.endDate}`
          .toLowerCase()
          .includes(searchLower)) ||
      // Employee details from map
      (empDetails.mobileNumber &&
        empDetails.mobileNumber.toLowerCase().includes(searchLower));

    const matchesLocation =
      !locationFilter ||
      (empDetails.locationName &&
        empDetails.locationName
          .toLowerCase()
          .includes(locationFilter.toLowerCase()));

    const matchesShift =
      !shiftFilter ||
      (empDetails.shiftName &&
        empDetails.shiftName.toLowerCase() === shiftFilter.toLowerCase());

    const matchesMonth =
      !monthFilter ||
      getSalaryMonth(salary.startDate, salary.endDate).toLowerCase() ===
        monthFilter.toLowerCase();

    // Days filter matching
    const calculatedDays = calculateDaysBetween(
      salary.startDate,
      salary.endDate
    );
    const matchesDays =
      !daysFilter ||
      (daysFilter === "1-7"
        ? calculatedDays >= 1 && calculatedDays <= 7
        : daysFilter === "8-15"
        ? calculatedDays >= 8 && calculatedDays <= 15
        : daysFilter === "16-23"
        ? calculatedDays >= 16 && calculatedDays <= 23
        : daysFilter === "24-31"
        ? calculatedDays >= 24 && calculatedDays <= 31
        : daysFilter === "32+"
        ? calculatedDays >= 32
        : false);

    return (
      matchesSearch &&
      matchesLocation &&
      matchesShift &&
      matchesMonth &&
      matchesDays
    );
  });

  const totalPages = Math.ceil(filteredSalaryData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredSalaryData.slice(startIndex, endIndex);

  // Fixed EmployeeSalaryTable component
  const EmployeeSalaryTable = ({
    paginatedData,
    employeeDetailsMap,
    missingDetailsMap,
    isSuperAdmin,
    onOpenPdf,
    onGenerate,
    onUpdateStatus,
    onDownload,
    downloadingPdf,
    onDelete,
    onEdit,
  }) => {
    return (
      <div style={{ overflowX: "auto" }}>
        <Table
          striped
          bordered
          hover
          responsive
          className="align-middle text-center"
        >
          <thead>
            <tr id="text-white-important">
              <th id="text-white-important">SI</th>
              <th id="text-white-important">Month</th>
              <th id="text-white-important">ID</th>
              <th id="text-white-important">Name</th>
              <th id="text-white-important">Mobile</th>
              <th id="text-white-important">Location</th>
              <th id="text-white-important">Shift</th>
              <th id="text-white-important">Pay Period</th>
              <th id="text-white-important">Working Days</th>
              <th id="text-white-important">Regular Hours</th>
              <th id="text-white-important">Overtime</th>
              <th id="text-white-important">Gross Salary</th>
              <th id="text-white-important" style={{ width: "120px" }}>
                Status
              </th>
              {(canAdd || canDelete || canEdit || canView) && (
                <th id="text-white-important" style={{ width: "200px" }}>
                  Actions
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((salary, idx) => {
                const hasPayslip = !!salary.payslipPdf;
                const isPendingRequest = salary.requestforpayslip === "pending";

                const empDetails = employeeDetailsMap[salary.id] || {
                  locationName: "N/A",
                  shiftName: "N/A",
                  mobileNumber: salary.mobileNumber || "N/A",
                };

                const missing = missingDetailsMap[salary.id];
                const pdfUrl = hasPayslip
                  ? `https://api.dremerz.net/uploads/${salary.payslipPdf}`
                  : null;

                // STATUS ELEMENT
                let statusEl;
                if (hasPayslip) {
                  statusEl = (
                    <span className="text-success d-flex align-items-center justify-content-center">
                      <FaCheckCircle className="me-1" /> Generated
                    </span>
                  );
                } else if (isPendingRequest) {
                  const hasMissing =
                    missing &&
                    (missing.basic || missing.bank || missing.salary);
                  statusEl = (
                    <div className="d-flex flex-column align-items-center">
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => onUpdateStatus(salary.id, salary)}
                      >
                        Update
                      </Button>
                      {hasMissing && (
                        <small
                          className="text-danger"
                          style={{ fontSize: "0.7rem" }}
                        >
                          Missing{" "}
                          {[
                            ...(missing.basic ? ["Basic"] : []),
                            ...(missing.bank ? ["Bank"] : []),
                            ...(missing.salary ? ["Salary"] : []),
                          ].join(", ")}{" "}
                          details
                        </small>
                      )}
                      {missing?.error && (
                        <small
                          className="text-danger fw-bold"
                          style={{ fontSize: "0.7rem" }}
                        >
                          {missing.error === "No employee data found"
                            ? "Employee record not found"
                            : missing.error}
                        </small>
                      )}
                    </div>
                  );
                } else {
                  statusEl = (
                    <span className="text-warning d-flex align-items-center justify-content-center">
                      <FaExclamationCircle className="me-1" /> Pending
                    </span>
                  );
                }

                return (
                  <tr key={salary.id}>
                    <td>{filteredSalaryData.length - idx}</td>
                    <td>{salaryMonth(salary.startDate, salary.endDate)}</td>
                    <td>{salary.employee_id || "N/A"}</td>
                    <td>{salary.userName || "N/A"}</td>
                    <td>{empDetails.mobileNumber}</td>
                    <td>{empDetails.locationName}</td>
                    <td>{empDetails.shiftName}</td>

                    <td style={{ minWidth: "150px" }}>
                      <div>
                        <div>
                          ({formatDate(salary.startDate)}) - (
                          {formatDate(salary.endDate)})
                        </div>
                        <small className="text-muted">
                          {daysBetween(salary.startDate, salary.endDate)} days
                        </small>
                      </div>
                    </td>

                    <td>{salary.workingDays || "N/A"}</td>
                    <td>{formatHrs(salary.normaltotalWorkingHours)}</td>
                    <td>{formatHrs(salary.ottotalWorkingHours)}</td>

                    <td>
                      {(() => {
                        const amt = parseFloat(
                          salary.overallcalculatedSalary || 0
                        );
                        return isNaN(amt)
                          ? "₹0"
                          : `₹${Math.round(amt).toLocaleString("en-IN")}`;
                      })()}
                    </td>

                    <td>{statusEl}</td>

                    {(canAdd || canDelete || canEdit || canView) && (
                      <td>
                        <div className="d-flex flex-wrap gap-1 justify-content-center">
                          {!hasPayslip ? (
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => onGenerate(salary.id)}
                              disabled={!isSuperAdmin}
                              title={
                                !isSuperAdmin
                                  ? "Only superadmin can generate payslips"
                                  : "Generate Payslip"
                              }
                            >
                              Select
                            </Button>
                          ) : (
                            <>
                              {canView && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleOpenPdfModal(pdfUrl)}
                                >
                                  View
                                </Button>
                              )}
                              {canView && (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() =>
                                    handleDownloadPdf(
                                      pdfUrl,
                                      salary.id,
                                      salary.userName || "employee"
                                    )
                                  }
                                >
                                  Download
                                </Button>
                              )}
                              {canEdit && (
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleEditPayslip(salary.id)}
                                  disabled={!isSuperAdmin}
                                >
                                  Edit
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteClick(salary)}
                                  disabled={!isSuperAdmin}
                                >
                                  Delete
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="14" className="text-center py-4">
                  No salary data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  if (loading && salaryData.length === 0) {
    return (
      <div className="container-fluid px-4 py-4">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <div className="text-center">
            <Spinner className="text-primary" size="lg" />
            <p className="mt-3 text-muted">Loading salary data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "100vh" }}
      >
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
        <Button
          variant="primary"
          onClick={async () => {
            try {
              setError("");
              await fetchSalaryData();
            } catch (error) {
              console.error("Retry failed:", error);
              addToast("Retry failed. Please refresh the page.", "error");
            }
          }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  // Export to Excel function
  const exportToExcel = () => {
    if (filteredSalaryData.length === 0) {
      addToast("No data available to export", "warning");
      return;
    }

    try {
      const data = filteredSalaryData.map((salary, index) => {
        const empDetails = employeeDetailsMap[salary.id] || {};
        const hasPayslip = !!salary.payslipPdf;
        const isPendingRequest = salary.requestforpayslip === "pending";

        let status = "Pending";
        if (hasPayslip) {
          status = "Generated";
        } else if (isPendingRequest) {
          status = "Pending Request";
        }

        return {
          "S.No": index + 1,
          Month: getSalaryMonth(salary.startDate, salary.endDate),
          "Employee ID": salary.employee_id || "N/A",
          Name: salary.userName || "N/A",
          "Mobile Number":
            empDetails.mobileNumber || salary.mobileNumber || "N/A",
          Location: empDetails.locationName || "N/A",
          Shift: empDetails.shiftName || "N/A",
          "Pay Period": `${formatDate(salary.startDate)} to ${formatDate(
            salary.endDate
          )}`,
          Days: calculateDaysBetween(salary.startDate, salary.endDate),
          "Working Days": salary.workingDays || "N/A",
          "Regular Hours": formatTimeDuration(salary.normaltotalWorkingHours),
          Overtime: formatTimeDuration(salary.ottotalWorkingHours),
          "Gross Salary": (() => {
            try {
              const salaryAmount = parseFloat(
                salary.overallcalculatedSalary || 0
              );
              if (isNaN(salaryAmount)) return "₹0";
              return `₹${Math.round(salaryAmount).toLocaleString("en-IN")}`;
            } catch (error) {
              return "₹0";
            }
          })(),
          Status: status,
          "Payslip Generated": hasPayslip ? "Yes" : "No",
          "Request Status": salary.requestforpayslip || "N/A",
          Response: salary.payslipResponse || "N/A",
        };
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Payslips");

      // Create filename with timestamp and filter info
      const timestamp = new Date().toISOString().split("T")[0];
      let filename = `payslips_${timestamp}`;

      if (payslipFilter !== "all") {
        filename += `_${payslipFilter}`;
      }
      if (searchTerm) {
        filename += `_${searchTerm.replace(/\s+/g, "_")}`;
      }
      if (locationFilter) {
        filename += `_${locationFilter.replace(/\s+/g, "_")}`;
      }

      filename += ".xlsx";

      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      addToast("Failed to export Excel file", "error");
    }
  };

  // Export to PDF function
  const exportToPDF = () => {
    if (filteredSalaryData.length === 0) {
      addToast("No data available to export", "warning");
      return;
    }

    try {
      const doc = new jsPDF("landscape");

      // Add title
      doc.setFontSize(16);
      doc.text("Payslip Management Report", 14, 15);

      // Add export date and filter info
      doc.setFontSize(10);
      doc.text(`Exported on: ${new Date().toLocaleString("en-IN")}`, 14, 22);
      doc.text(`Total Records: ${filteredSalaryData.length}`, 14, 28);

      // Add filter information
      let filterY = 34;
      if (payslipFilter !== "all") {
        doc.text(
          `Filter: ${
            payslipFilter.charAt(0).toUpperCase() + payslipFilter.slice(1)
          } Payslips`,
          14,
          filterY
        );
        filterY += 6;
      }
      if (searchTerm) {
        doc.text(`Search: ${searchTerm}`, 14, filterY);
        filterY += 6;
      }
      if (locationFilter) {
        doc.text(`Location: ${locationFilter}`, 14, filterY);
        filterY += 6;
      }
      if (shiftFilter) {
        doc.text(`Shift: ${shiftFilter}`, 14, filterY);
        filterY += 6;
      }

      // Add summary statistics
      const generatedCount = filteredSalaryData.filter(
        (s) => !!s.payslipPdf
      ).length;
      const pendingCount = filteredSalaryData.filter(
        (s) => !s.payslipPdf
      ).length;
      const totalSalary = filteredSalaryData.reduce((sum, s) => {
        try {
          const salaryAmount = parseFloat(s.overallcalculatedSalary || 0);
          return sum + (isNaN(salaryAmount) ? 0 : salaryAmount);
        } catch (error) {
          return sum;
        }
      }, 0);

      doc.text(`Generated: ${generatedCount}`, 150, 34);
      doc.text(`Pending: ${pendingCount}`, 150, 40);
      doc.text(
        `Total Salary: ₹${Math.round(totalSalary).toLocaleString("en-IN")}`,
        150,
        46
      );

      // Prepare table data
      const tableData = filteredSalaryData.map((salary, index) => {
        const empDetails = employeeDetailsMap[salary.id] || {};

        return [
          (index + 1).toString(),
          getSalaryMonth(salary.startDate, salary.endDate),
          salary.employee_id || "N/A",
          salary.userName || "N/A",
          empDetails.locationName || "N/A",
          `${formatDate(salary.startDate)} to ${formatDate(salary.endDate)}`,
          calculateDaysBetween(salary.startDate, salary.endDate).toString(),
          salary.workingDays || "N/A",
          formatTimeDuration(salary.normaltotalWorkingHours),
          formatTimeDuration(salary.ottotalWorkingHours),
          (() => {
            try {
              const salaryAmount = parseFloat(
                salary.overallcalculatedSalary || 0
              );
              if (isNaN(salaryAmount)) return "₹0";
              return `₹${Math.round(salaryAmount).toLocaleString("en-IN")}`;
            } catch (error) {
              return "₹0";
            }
          })(),
          salary.payslipPdf ? "Generated" : "Pending",
        ];
      });

      // Create table
      autoTable(doc, {
        startY: filterY > 34 ? filterY + 6 : 52,
        head: [
          [
            "S.No",
            "Month",
            "Emp ID",
            "Name",
            "Location",
            "Pay Period",
            "Days",
            "Work Days",
            "Regular Hrs",
            "OT Hrs",
            "Gross Salary",
            "Status",
          ],
        ],
        body: tableData,
        theme: "grid",
        styles: { fontSize: 7 },
        headStyles: { fillColor: [66, 139, 202] },
        margin: { top: filterY > 34 ? filterY + 6 : 52 },
        columnStyles: {
          0: { cellWidth: 8 },
          1: { cellWidth: 15 },
          2: { cellWidth: 15 },
          3: { cellWidth: 20 },
          4: { cellWidth: 15 },
          5: { cellWidth: 25 },
          6: { cellWidth: 8 },
          7: { cellWidth: 10 },
          8: { cellWidth: 12 },
          9: { cellWidth: 10 },
          10: { cellWidth: 15 },
          11: { cellWidth: 12 },
        },
      });

      // Add footer with page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: "center" }
        );
      }

      // Save the PDF
      const timestamp = new Date().toISOString().split("T")[0];
      let filename = `payslips_${timestamp}`;

      if (payslipFilter !== "all") {
        filename += `_${payslipFilter}`;
      }
      if (searchTerm) {
        filename += `_${searchTerm.replace(/\s+/g, "_")}`;
      }

      filename += ".pdf";

      doc.save(filename);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      addToast("Failed to export PDF file", "error");
    }
  };

  return (
    <Container className="px-4 pt-0">
      <Row className="mb-2">
        <Card className="p-3 border-0">
          <div className="row align-items-center mb-2 border rounded bg-light py-2">
            <div className="col-12 col-md-6">
              <h5 className="mb-0 d-flex flex-start">
                Generate Payslips For Employees
              </h5>
            </div>
            <div className="col-12 col-md-6 mt-2 md-mt-0 text-end">
              {filteredSalaryData.length > 0 && (
                <div className="d-flex gap-2 justify-content-end">
                  <button
                    className="btn btn-outline-success btn-sm"
                    onClick={exportToExcel}
                    disabled={filteredSalaryData.length === 0}
                    title="Export to Excel"
                  >
                    Export Excel
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={exportToPDF}
                    disabled={filteredSalaryData.length === 0}
                    title="Export to PDF"
                  >
                    Export PDF
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="row align-items-center mb-3 border rounded bg-light py-3 px-2">
            {/* Search and Filters */}
            <div className="col-12 col-md-9 mb-2 mb-md-0">
              <div className="row g-3">
                {/* Search Input */}
                <div className="col-12 col-md-3">
                  <Form.Group className="mb-0">
                    <Form.Label className="small text-muted mb-1">
                      Search
                    </Form.Label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light border-end-0">
                        <Search size={16} className="text-muted" />
                      </span>
                      <Form.Control
                        type="text"
                        placeholder="Search here..."
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                    </div>
                  </Form.Group>
                </div>

                {/* Location Filter */}
                <div className="col-12 col-md-3">
                  <Form.Group className="mb-0 position-relative">
                    <Form.Label className="small text-muted mb-1">
                      Location
                    </Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Select or type location..."
                      value={locationFilter}
                      onChange={(e) => {
                        setLocationFilter(e.target.value);
                        setShiftFilter("");
                        setCurrentPage(1);
                      }}
                      onFocus={() => setShowLocationDropdown(true)}
                      onBlur={() =>
                        setTimeout(() => setShowLocationDropdown(false), 300)
                      }
                    />
                    {showLocationDropdown && (
                      <div
                        className="dropdown-menu show w-100"
                        style={{ maxHeight: "300px", overflowY: "auto" }}
                        onMouseEnter={() => setShowLocationDropdown(true)}
                        onMouseLeave={() => setShowLocationDropdown(false)}
                      >
                        {locationsData
                          .filter((location) =>
                            location.locationName
                              .toLowerCase()
                              .includes(locationFilter.toLowerCase())
                          )
                          .map((location) => (
                            <button
                              key={location.locationName}
                              className="dropdown-item"
                              type="button"
                              onClick={() => {
                                setLocationFilter(location.locationName);
                                setShowLocationDropdown(false);
                              }}
                            >
                              {location.locationName}
                            </button>
                          ))}
                      </div>
                    )}
                  </Form.Group>
                </div>

                {/* Shift Filter */}
                <div className="col-6 col-md-2">
                  <Form.Group className="mb-0">
                    <Form.Label className="small text-muted mb-1">
                      Shift
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={shiftFilter}
                      onChange={(e) => {
                        setShiftFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      disabled={!locationFilter}
                    >
                      <option value="">All Shifts</option>
                      {locationFilter &&
                        (() => {
                          const selectedLocation = locationsData.find(
                            (loc) => loc.locationName === locationFilter
                          );
                          if (selectedLocation && selectedLocation.shifts) {
                            try {
                              const shifts = JSON.parse(
                                selectedLocation.shifts
                              );
                              return shifts.map((shift) => (
                                <option
                                  key={shift.shiftName}
                                  value={shift.shiftName}
                                >
                                  {shift.shiftName}
                                </option>
                              ));
                            } catch (error) {
                              console.error("Error parsing shifts:", error);
                              return null;
                            }
                          }
                          return null;
                        })()}
                    </Form.Select>
                  </Form.Group>
                </div>

                {/* Month Filter */}
                <div className="col-6 col-md-2">
                  <Form.Group className="mb-0">
                    <Form.Label className="small text-muted mb-1">
                      Month
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={monthFilter}
                      onChange={(e) => {
                        setMonthFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Months</option>
                      {[
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                      ].map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                {/* Clear Button */}
                <div className="col-5 col-md-2">
                  <Form.Group className="mb-0">
                    <Form.Label className="small text-muted mb-1 invisible">
                      Clear
                    </Form.Label>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="w-100"
                      type="button"
                      onClick={clearAllFilters}
                      disabled={
                        !searchTerm &&
                        !locationFilter &&
                        !shiftFilter &&
                        !monthFilter &&
                        payslipFilter === "all"
                      }
                    >
                      Clear
                    </Button>
                  </Form.Group>
                </div>
              </div>
            </div>

            {/* Payslip Filter Buttons */}
            <div className="col-12 col-md-3 mt-3 mt-md-4">
              <Form.Group className="mb-0 text-center text-md-end">
                <div className="btn-group btn-group-sm" role="group">
                  <Button
                    variant={
                      payslipFilter === "all" ? "primary" : "outline-primary"
                    }
                    onClick={() => {
                      setPayslipFilter("all");
                      setCurrentPage(1);
                    }}
                  >
                    All
                  </Button>
                  <Button
                    variant={
                      payslipFilter === "generated"
                        ? "primary"
                        : "outline-primary"
                    }
                    onClick={() => {
                      setPayslipFilter("generated");
                      setCurrentPage(1);
                    }}
                  >
                    Generated
                  </Button>
                  <Button
                    variant={
                      payslipFilter === "pending"
                        ? "primary"
                        : "outline-primary"
                    }
                    onClick={() => {
                      setPayslipFilter("pending");
                      setCurrentPage(1);
                    }}
                  >
                    Pending
                  </Button>
                </div>
              </Form.Group>
            </div>
          </div>

          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            {/* Items per page selector */}
            <div className="d-flex align-items-center gap-2 order-2 order-md-1">
              <span className="text-muted small">Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "120px" }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-muted small d-none d-sm-inline">
                entries
              </span>
              <span className="text-muted small d-inline d-sm-none">items</span>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <nav
                aria-label="Payslip pagination"
                className="order-1 order-md-2"
              >
                <ul className="pagination pagination-sm mb-0 flex-wrap justify-content-center">
                  {/* First Button - Hidden on mobile */}
                  <li
                    className={`page-item d-none d-sm-block ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </button>
                  </li>

                  {/* Previous Button */}
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <span className="d-none d-sm-inline">Previous</span>
                      <span className="d-inline d-sm-none">‹</span>
                    </button>
                  </li>

                  {/* Mobile Page Indicator */}
                  <li className="page-item d-md-none">
                    <span className="page-link text-dark bg-light">
                      {currentPage} / {totalPages}
                    </span>
                  </li>

                  {/* Desktop Page Numbers */}
                  {(() => {
                    const pageNumbers = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(
                      1,
                      currentPage - Math.floor(maxVisiblePages / 2)
                    );
                    let endPage = Math.min(
                      totalPages,
                      startPage + maxVisiblePages - 1
                    );

                    if (endPage - startPage < maxVisiblePages - 1) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pageNumbers.push(
                        <li
                          key={i}
                          className={`page-item d-none d-md-block ${
                            currentPage === i ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }
                    return pageNumbers;
                  })()}

                  {/* Next Button */}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <span className="d-none d-sm-inline">Next</span>
                      <span className="d-inline d-sm-none">›</span>
                    </button>
                  </li>

                  {/* Last Button - Hidden on mobile */}
                  <li
                    className={`page-item d-none d-sm-block ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      Last
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </Card>
      </Row>

      <Row>
        <Col>
          <div className="d-none d-md-block">
            <EmployeeSalaryTable
              paginatedData={paginatedData}
              employeeDetailsMap={employeeDetailsMap}
              missingDetailsMap={missingDetailsMap}
              isSuperAdmin={isSuperAdmin}
              onOpenPdf={handleOpenPdfModal}
              onGenerate={handleGeneratePayslip}
              onUpdateStatus={handleUpdateStatusClick}
              onDownload={handleDownloadPdf}
              downloadingPdf={downloadingPdf}
              onDelete={handleDeleteClick}
              onEdit={handleEditPayslip}
            />
          </div>

          <div className="d-block d-md-none">
            {filteredSalaryData.length > 0 ? (
              filteredSalaryData
                .slice(startIndex, endIndex)
                .map((salary, index) => {
                  const hasPayslip = !!salary.payslipPdf;
                  const pdfUrl = salary.payslipPdf
                    ? `https://api.dremerz.net/uploads/${salary.payslipPdf}`
                    : null;

                  const empDetails = employeeDetailsMap[salary.id] || {
                    locationName: "N/A",
                    shiftName: "N/A",
                    mobileNumber: salary.mobileNumber || "N/A",
                  };

                  return (
                    <div
                      key={salary.id}
                      className="card mb-3 shadow-sm border border-light"
                    >
                      <div className="card-body p-2">
                        <Table bordered size="sm" className="mb-2">
                          <tbody>
                            <tr>
                              <td className="fw-bold">SI</td>
                              <td>
                                {filteredSalaryData.length -
                                  (startIndex + index)}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Month</td>
                              <td>
                                {getSalaryMonth(
                                  salary.startDate,
                                  salary.endDate
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Employee ID</td>
                              <td>{salary.employee_id || "N/A"}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Name</td>
                              <td>{salary.userName || "N/A"}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Mobile</td>
                              <td>{empDetails.mobileNumber}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Location</td>
                              <td>{empDetails.locationName}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Shift</td>
                              <td>{empDetails.shiftName}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Pay Period</td>
                              <td>
                                ({formatDate(salary.startDate)}) - (
                                {formatDate(salary.endDate)})
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Working Days</td>
                              <td>{salary.workingDays || "N/A"}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Regular Hours</td>
                              <td>
                                {formatTimeDuration(
                                  salary.normaltotalWorkingHours
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Overtime</td>
                              <td>
                                {formatTimeDuration(salary.ottotalWorkingHours)}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Gross Salary</td>
                              <td>
                                {(() => {
                                  try {
                                    const salaryAmount = parseFloat(
                                      salary.overallcalculatedSalary || 0
                                    );
                                    if (isNaN(salaryAmount)) return "₹0";
                                    return `₹${Math.round(
                                      salaryAmount
                                    ).toLocaleString("en-IN")}`;
                                  } catch {
                                    return "₹0";
                                  }
                                })()}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Status</td>
                              <td>
                                {hasPayslip ? (
                                  <span className="text-success fw-semibold">
                                    Generated
                                  </span>
                                ) : (
                                  <span className="text-warning fw-semibold">
                                    Pending
                                  </span>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </Table>

                        {/* Action Buttons */}
                        {(canAdd || canDelete || canEdit || canView) && (
                          <div className="d-flex justify-content-between">
                            {!hasPayslip ? (
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="flex-fill me-1"
                                onClick={() => handleGeneratePayslip(salary.id)}
                                disabled={currentUserRole !== "superadmin"}
                              >
                                Generate
                              </Button>
                            ) : (
                              <>
                                {canView && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="flex-fill me-1 mt-2"
                                    onClick={() => handleOpenPdfModal(pdfUrl)}
                                  >
                                    View
                                  </Button>
                                )}
                                {canView && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="flex-fill ms-1 mt-2"
                                    onClick={() =>
                                      handleDownloadPdf(
                                        pdfUrl,
                                        salary.id,
                                        salary.userName || "employee"
                                      )
                                    }
                                  >
                                    Download
                                  </Button>
                                )}

                                {canEdit && (
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    className="flex-fill ms-1 mt-2"
                                    onClick={() =>
                                      handleGeneratePayslip(salary.id)
                                    }
                                    disabled={!isSuperAdmin}
                                  >
                                    Edit
                                  </Button>
                                )}
                                {canDelete && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="flex-fill ms-1 mt-2"
                                    onClick={() => handleDeleteClick(salary)}
                                    disabled={!isSuperAdmin}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="text-center text-muted py-3">
                No payslip data found
              </div>
            )}
          </div>

          <div className="text-muted d-flex justify-content-end align-items-center">
            Showing {startIndex + 1} to{" "}
            {Math.min(endIndex, filteredSalaryData.length)} of{" "}
            {filteredSalaryData.length} entries
            {(searchTerm ||
              locationFilter ||
              shiftFilter ||
              payslipFilter !== "all") && (
              <span className="ms-2">
                (filtered from {salaryData.length} total entries)
              </span>
            )}
          </div>
        </Col>
      </Row>

      {/* Status Update Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Payslip Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="statusMessage">
            <Form.Label>Response Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={statusMessage}
              onChange={(e) => setStatusMessage(e.target.value)}
              placeholder="Enter the reason why payslip can't be generated..."
            />
            <Form.Text className="text-muted">
              This message will be sent to the employee
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleStatusSubmit}
            disabled={isUpdatingStatus || !statusMessage.trim()}
          >
            {isUpdatingStatus ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              "Update Status"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* PDF Modal */}
      <Modal
        show={showPdfModal}
        onHide={handleClosePdfModal}
        size="lg"
        centered
        className="pdf-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Payslip</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: "80vh" }}>
          {currentPdfUrl ? (
            <iframe
              src={`https://docs.google.com/gview?url=${encodeURIComponent(
                currentPdfUrl
              )}&embedded=true`}
              title="Payslip PDF"
              width="100%"
              height="100%"
              style={{ border: "none" }}
              onError={() => {
                console.error("Failed to load PDF:", currentPdfUrl);
                addToast(
                  "Failed to load PDF. Please try downloading instead.",
                  "warning"
                );
              }}
            />
          ) : (
            <div className="text-center p-5">
              <h5>PDF not available</h5>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClosePdfModal}>
            Close
          </Button>
          {currentPdfUrl && (
            <Button
              variant="primary"
              onClick={() =>
                handleDownloadPdf(
                  currentPdfUrl,
                  Date.now(), // unique temporary ID (not null!)
                  "Payslip"
                )
              }
              disabled={downloadingPdf !== null}
              className="d-flex align-items-center gap-1"
            >
              {downloadingPdf !== null ? (
                <>
                  <Spinner as="span" animation="border" size="sm" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Download size={14} />
                  <span>Download</span>
                </>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the payslip for:</p>
          <strong>{salaryToDelete?.userName || "N/A"}</strong>
          <p className="text-muted mt-2">
            Pay Period: {formatDate(salaryToDelete?.startDate)} to{" "}
            {formatDate(salaryToDelete?.endDate)}
          </p>
          <Alert variant="warning" className="mt-3">
            This action cannot be undone!
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PayslipManagement;
